import { combineReducers } from 'redux'
import {
  LinkageAppState,
  LinkageProgress,
  LinkageProgressCollection,
  LinkageSession,
  LinkageSessionCollection,
  LinkageSessionId,
  LiveUpdateAppState,
  OperationConfirmation,
  ProviderAccountState,
  ProviderAccountStatus,
} from 'interfaces/linkage'
import { Reducer } from 'interfaces'
import { createEmptyCollection, removeByKeyFromCollection, setItemInCollection } from 'helpers/normalizeHelpers'
import {
  AUTHENTICATE_LINKAGE,
  CONFIRM_MIXED_ACCOUNT_OPERATION,
  FLUSH_LIVE_UPDATE,
  GET_DETAILED_PROVIDER,
  GET_MIXED_ACCOUNTS,
  KILL_LINKAGE_SESSION,
  PERFORM_MIXED_ACCOUNT_OPERATION,
  POLL_PROVIDER_ACCOUNT,
  REQUEST_LINKAGE_SESSION,
  SET_LINKAGE_PROGRESS,
  START_LINKAGE_SESSION,
} from 'actions/linkageActions'
import { isFailAction, isRequestAction, isSuccessAction } from 'helpers/actionsHelpers'
import { extractLinkageEntityFromCid } from 'helpers/linkageHelpers'
import { FORM_STOP_SUBMIT } from 'actions/formActions'
import { FORM_LINKAGE_AUTHENTICATION } from 'constants/forms'
import { PAGE_CHANGED } from 'actions/globalActions'
import { SETTINGS_ACCOUNTS_URL } from 'tangle-shared'
import { needDiscardData, PROVIDER_ACCOUNTS_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'

const SHOW_PROGRESS_STATUS = new Set<ProviderAccountStatus>(['verifying', 'gathering', 'retrieving', 'updating'])
const DROP_PROGRESS_STATUS = new Set<ProviderAccountStatus>(['failed_verification', 'failed'])
const FINISH_PROGRESS_STATUS = new Set<ProviderAccountStatus>(['ready', 'failed_retrieval'])

// TODO: test
export const setLinkageProgress = (state: LinkageAppState['progress'], providerAccountState: ProviderAccountState): LinkageAppState['progress'] => {
  const { cid, status } = providerAccountState

  let nextProgress: LinkageProgress = {
    cid,
    value: 0,
    state: 'loading',
  }

  const prevLinkageProgress = state.byKey[cid]

  if (!prevLinkageProgress && SHOW_PROGRESS_STATUS.has(status)) {
    return setItemInCollection(state, nextProgress, 'cid')
  }

  if (DROP_PROGRESS_STATUS.has(status)) {
    return removeByKeyFromCollection<LinkageProgress, 'cid'>(state, cid)
  }

  if (prevLinkageProgress) {
    if (status !== 'mfa_required' && !FINISH_PROGRESS_STATUS.has(status)) {
      nextProgress = {
        ...prevLinkageProgress,
        state: 'loading',
      }
      return setItemInCollection(state, nextProgress, 'cid', { checkEquality: true })
    }

    nextProgress = {
      ...prevLinkageProgress,
      state: status === 'mfa_required' ? 'stopped' : 'finished',
    }

    return setItemInCollection(state, nextProgress, 'cid')
  }

  return state
}


export const progressReducer: Reducer<LinkageAppState['progress']> = (state = createEmptyCollection(), action) => {
  if (needDiscardData(PROVIDER_ACCOUNTS_DISCARD_TRIGGERS)(action)) {
    return createEmptyCollection()
  }

  if (action.type === GET_MIXED_ACCOUNTS && isSuccessAction(action)) {
    const { providerAccountStates } = action.payload.response
    const nextProgresses = providerAccountStates.allKeys.reduce<LinkageProgressCollection>(
      (result, key) => setLinkageProgress(result, providerAccountStates.byKey[key]),
      state
    )

    return nextProgresses
  }

  if (action.type === AUTHENTICATE_LINKAGE && isSuccessAction(action)
    || action.type === POLL_PROVIDER_ACCOUNT && isSuccessAction(action)) {
    const { providerAccountState } = action.payload.response

    return setLinkageProgress(state, providerAccountState)
  }

  if (action.type === SET_LINKAGE_PROGRESS) {
    const { cid, value } = action.payload
    if (!value) {
      return removeByKeyFromCollection<LinkageProgress, 'cid'>(state, cid)
    }

    const prevLinkageProgress = state.byKey[cid]
    return prevLinkageProgress
      ? setItemInCollection(state, { ...prevLinkageProgress, value }, 'cid')
      : state
  }

  return state
}

const DEFAULT_LIVE_UPDATE_STATE: LiveUpdateAppState = {
  isFlushing: false,
}

const liveUpdateReducer: Reducer<LinkageAppState['liveUpdate']> = (state = DEFAULT_LIVE_UPDATE_STATE, action) => {
  if (action.type === FLUSH_LIVE_UPDATE) {
    if (isSuccessAction(action) || isFailAction(action)) {
      return DEFAULT_LIVE_UPDATE_STATE
    }

    if (isRequestAction(action)) {
      return {
        ...state,
        isFlushing: true,
      }
    }
  }

  return state
}

const removeFirstSessionIfMatched = (state: LinkageSessionCollection, sessionId: LinkageSessionId): LinkageSessionCollection =>
  sessionId === state.allKeys[0] ? removeByKeyFromCollection<LinkageSession, 'sessionId'>(state, sessionId) : state

const sessionsReducer: Reducer<LinkageAppState['sessions']> = (state = createEmptyCollection(), action) => {
  if (action.type === START_LINKAGE_SESSION && isSuccessAction(action)) {
    const { response, params } = action.payload
    return setItemInCollection(state, {
      ...response,
      cid: params.cid,
    }, 'sessionId')
  }

  if (action.type === KILL_LINKAGE_SESSION) {
    return removeByKeyFromCollection<LinkageSession, 'sessionId'>(state, action.payload.sessionId)
  }

  if (action.type === AUTHENTICATE_LINKAGE && isSuccessAction(action)) {
    return removeFirstSessionIfMatched(state, action.payload.params.session.sessionId)
  }

  if (action.type === FORM_STOP_SUBMIT && action.meta.form === FORM_LINKAGE_AUTHENTICATION) {
    if (!state || !state.allKeys.length) {
      return state
    }

    return setItemInCollection<LinkageSession, 'sessionId'>(state, {
      ...state.byKey[state.allKeys[0]],
      loading: false,
    }, 'sessionId')
  }

  if (action.type === GET_DETAILED_PROVIDER) {
    const { session } = action.payload.params

    if (isSuccessAction(action)) {
      const { provider } = action.payload.response
      const prevEntityFromCid = extractLinkageEntityFromCid(session.cid)
      if (!prevEntityFromCid) {
        return state
      }

      const nextSession: LinkageSession = {
        cid: prevEntityFromCid.type === 'provider-account' ? session.cid : provider.cid,
        sessionId: session.sessionId,
      }

      return setItemInCollection(state, nextSession, 'sessionId')
    }

    if (isFailAction(action)) {
      return removeFirstSessionIfMatched(state, action.payload.params.session.sessionId)
    }
  }

  if (action.type === AUTHENTICATE_LINKAGE) {
    const { session } = action.payload.params
    if (isRequestAction(action)) {
      return setItemInCollection<LinkageSession, 'sessionId'>(state, {
        ...session,
        loading: true,
      }, 'sessionId')
    }

    if (isFailAction(action)) {
      return removeFirstSessionIfMatched(state, action.payload.params.session.sessionId)
    }
  }

  if (action.type === PAGE_CHANGED && action.payload.prev === SETTINGS_ACCOUNTS_URL) {
    return createEmptyCollection()
  }

  return state
}

const confirmationsReducer: Reducer<LinkageAppState['confirmations']> = (state = createEmptyCollection(), action) => {
  if (action.type === CONFIRM_MIXED_ACCOUNT_OPERATION) {
    const { mixedAccount } = action.payload

    if (action.payload.operation === null) {
      return removeByKeyFromCollection<OperationConfirmation, 'cid'>(state, mixedAccount.cid)
    }

    const { operation } = action.payload
    const entityFromCid = extractLinkageEntityFromCid(mixedAccount.cid)
    if (!entityFromCid) {
      return state
    }

    return setItemInCollection(state, {
      cid: mixedAccount.cid,
      operation,
      entityType: entityFromCid.type,
    }, 'cid')
  }

  if (action.type === PERFORM_MIXED_ACCOUNT_OPERATION && isRequestAction(action)) {
    return removeByKeyFromCollection<OperationConfirmation, 'cid'>(state, action.payload.params.account.cid)
  }

  return state
}

export const requestedSessionReducer: Reducer<LinkageAppState['requestedSession']> = (state = null, action) => {
  if (action.type === REQUEST_LINKAGE_SESSION) {
    return action.payload.cid
  }

  if (action.type === START_LINKAGE_SESSION && action.payload.params.cid === state) {
    return null
  }

  return state
}

export default combineReducers<LinkageAppState>({
  progress: progressReducer,
  liveUpdate: liveUpdateReducer,
  sessions: sessionsReducer,
  confirmations: confirmationsReducer,
  requestedSession: requestedSessionReducer,
})
